/**
 * Recent dictations › Fix a word: the dictation's words become clickable;
 * pick the misheard one (and its neighbours, for a phrase), type the right
 * word, and Remember fixes the dictation and teaches the vocabulary. The
 * transcript is shown here only, never logged or reported.
 */
import { Fragment, useMemo, useState } from "react";
import { VOCABULARY_TEXT_MAX } from "@/api/voice-vocabulary";
import { Button, TextInput } from "@/components/ui";
import { chipVariants } from "@/components/ui/chip-variants";
import { voiceTokens } from "@/components/panels/dictated-word-substitution";
import type { DictationAnywhereRecord } from "@/desktop/dictation-anywhere/dictation-anywhere-native";
import {
  fixDictationAnywhereWord,
  learnedContext,
} from "@/desktop/dictation-vocabulary-native";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsVoiceVocabularyMessages } from "@/i18n/messages/settings-voice-vocabulary";
import { useDictationVocabularyStore } from "@/stores/dictation-vocabulary-store";
import {
  isSelected,
  selectWord,
  type WordSelection,
} from "./dictation-word-fix-selection";
import { SettingsFieldError } from "./settings-text-field";

export function VoiceDictationWordFix({
  record,
  onDone,
  onCancel,
}: {
  record: Pick<DictationAnywhereRecord, "id" | "raw">;
  onDone: () => void;
  onCancel: () => void;
}) {
  const t = useT(settingsVoiceVocabularyMessages);
  const tCommon = useT(commonMessages);
  const learn = useDictationVocabularyStore((state) => state.learn);
  const tokens = useMemo(() => voiceTokens(record.raw), [record.raw]);
  const [selection, setSelection] = useState<WordSelection | null>(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const typed = word.trim();

  async function remember() {
    if (!selection || !typed || busy) return;
    setBusy(true);
    setError(null);
    try {
      // The context is read from the dictation as heard, before the fix.
      // Learning comes first: if fixing the record then fails, Remember can
      // run again on the unchanged text and learning is idempotent.
      const context = await learnedContext(
        record.raw,
        selection.from,
        selection.to,
      );
      await learn({ word: typed, ...context, surface: "recent_dictation" });
      await fixDictationAnywhereWord(
        record.id,
        selection.from,
        selection.to,
        typed,
      );
      onDone();
    } catch {
      setError(t("rememberFailed"));
      setBusy(false);
    }
  }

  return (
    <span className="block whitespace-normal pb-0.5">
      <span className="-ml-0.5 block text-appkit-body leading-relaxed text-secondary">
        {tokens.map((token, index) => (
          <Fragment key={index}>
            {index > 0 ? " " : null}
            <button
              type="button"
              aria-pressed={isSelected(selection, index)}
              onClick={() => setSelection(selectWord(selection, index))}
              className={chipVariants({ variant: "word" })}
              data-testid={`dictation-fix-word-${index}`}
            >
              {token}
            </button>
          </Fragment>
        ))}
      </span>
      {selection ? (
        <span className="flex items-center gap-1.5 pb-0.5 pt-1.5">
          <TextInput
            shape="capsule"
            value={word}
            autoFocus
            maxLength={VOCABULARY_TEXT_MAX}
            placeholder={t("rightWord")}
            aria-label={t("rightWord")}
            onChange={(event) => setWord(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void remember();
            }}
            className="h-[30px] flex-1 px-3.5"
            data-testid="dictation-fix-input"
          />
          <Button
            size="sm"
            disabled={!typed || busy}
            onClick={() => void remember()}
            data-testid="dictation-fix-remember"
          >
            {t("remember")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid="dictation-fix-cancel"
          >
            {tCommon("cancel")}
          </Button>
        </span>
      ) : null}
      <span
        className="block text-appkit-small text-muted"
        data-testid="dictation-fix-hint"
      >
        {t(selection ? "fixHintMore" : "fixHint")}
      </span>
      <SettingsFieldError error={error} testId="dictation-fix-error" />
    </span>
  );
}
